/**
 * The switch between the till and the offline till, and the line that says
 * how the offline sales are getting on.
 *
 * Above the signed-in shell: with no connection the shell cannot check the
 * account and the ordinary till cannot even load its site, so a tablet that
 * is signed in gets the offline till in their place, not a patched one. Offline with
 * no snapshot, or on a plan without offline selling, the ordinary till stays
 * with a line saying the connection is down — it will say what failed.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { usePractice, useSession } from '@restaurant/api-client';
import { Button, Sheet } from '@restaurant/ui';

import { IdleLock } from '../lock';
import { PracticeNeedsConnection } from '../practice';
import { useConnection } from './connection';
import { clearRefusal, loadSnapshot, type QueuedSale, type Snapshot } from './store';
import { useQueue, useSync } from './sync';
import { OfflineTill } from './till';

export function OfflineGate({ children }: { children: ReactNode }) {
  const connection = useConnection();
  const session = useSession();
  const [snapshot, setSnapshot] = useState<Snapshot | null | undefined>(undefined);
  const queue = useQueue();
  const sync = useSync(connection);
  const practising = usePractice().practising;

  useEffect(() => {
    void loadSnapshot().then((s) => setSnapshot(s ?? null));
  }, [connection]);

  // Practice does not sell offline: an offline sale is sent as a real one
  // when the line is back, and a practice sale must never be.
  if (connection === 'offline' && session.isAuthenticated && practising) {
    return <PracticeNeedsConnection />;
  }

  if (connection === 'offline' && session.isAuthenticated && snapshot?.entitled) {
    // The idle lock too: a till left alone locks with or without a
    // connection, and offline it takes the PINs this tablet remembers.
    return (
      <>
        <OfflineTill snapshot={snapshot} />
        <IdleLock />
      </>
    );
  }

  return (
    <>
      {connection === 'offline' && (
        <p className="ol-line ol-line--down" role="status">
          No connection. The till needs one to work on this plan; it carries on when the line is
          back.
        </p>
      )}
      {connection === 'online' && queue.length > 0 && (
        <SyncLine queue={queue} sending={sync.sending} onRetry={sync.sendNow} />
      )}
      {children}
    </>
  );
}

function SyncLine({
  queue,
  sending,
  onRetry,
}: {
  queue: QueuedSale[];
  sending: boolean;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);
  const refused = queue.filter((q) => q.refused);
  const waiting = queue.length - refused.length;

  return (
    <>
      <p className={`ol-line${refused.length ? ' ol-line--refused' : ''}`} role="status">
        {waiting > 0 && (
          <span>
            {sending ? 'Sending' : 'Waiting to send'} {waiting} offline sale
            {waiting === 1 ? '' : 's'}…
          </span>
        )}
        {refused.length > 0 && (
          <button type="button" className="ol-line__btn" onClick={() => setOpen(true)}>
            {refused.length} offline sale{refused.length === 1 ? '' : 's'} could not be recorded
            — look
          </button>
        )}
      </p>
      {open && (
        <Sheet title="Offline sales not recorded" onDismiss={() => setOpen(false)}>
          <p className="pr__note">
            The server would not record these. They stay on this tablet until they are. Ask a
            manager: the usual reason is a day already closed, or a dish since removed.
          </p>
          <ul className="ol__refused">
            {refused.map((q) => (
              <li key={q.sale.client_id}>
                <strong>{q.receipt.reference}</strong> ·{' '}
                {new Date(q.sale.closed_at).toLocaleString()}
                <span className="ol__refused-why">{q.refused}</span>
                <Button
                  size="sm"
                  onClick={() => {
                    void clearRefusal(q.sale.client_id).then(onRetry);
                  }}
                >
                  Try again
                </Button>
              </li>
            ))}
          </ul>
        </Sheet>
      )}
    </>
  );
}
